import React, { useCallback, useMemo, useState } from 'react';
import { membershipUsersColumnsV2 } from '../../../tableColumns/membershipColumns';
import { history, toTitleCase } from '../../../utils';
import { IUserMembership } from '../../../ts/interface/membership.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import { ISortConfig } from '../../../ts/interface/components/redesing/table.interface';
import { SearchChip } from '../../../ts/interface/components/table.interface';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CardView from '../../../components/Redesing/card-view';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import useUrlState from '../../../hooks/use-url-state';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import {
  useUserMembershipsRelayQuery,
  UserMembershipSortInput,
  UserMembershipQueryFilter,
} from '../../../hooks/membership/query/V2/useUserMembershipsRelay';
import { mapUserMembershipToLegacy } from '../../../hooks/membership/query/V2/membershipMappers';
import { MenuItem } from '@mui/material';
import { MovaticFieldGroupSingleSelectFilter } from '../../../components/Redesing/movatic-field-group';
import { useUpdateEffect } from '../../../hooks/use-update-effect';

interface IMembershipListProps {
  authType: string;
  membershipId: string;
  systemId: string;
}

const SORT_KEY_MAP: Record<string, string> = {
  created_on: 'CREATED_ON',
  approved_on: 'APPROVED_ON',
  last_charged: 'LAST_CHARGED',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'expired', label: 'Expired' },
  { value: 'rejected', label: 'Rejected' },
];

type StatusKey = typeof STATUS_OPTIONS[number]['value'];

const ActiveMembershipList: React.FC<IMembershipListProps> = ({
  authType,
  membershipId,
  systemId,
}) => {
  const [exportData, setExportData] = useState<boolean>(false);
  const [members, setMembers] = useState<IUserMembership[]>([]);

  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(0);

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    status: 'active' as StatusKey,
    sortConfig: {
      key: 'created_on',
      direction: 'desc',
    } as ISortConfig,
  });

  const [chips, setChips] = useState<SearchChip[]>([]);
  const [statusValue, setStatusValue] = useState<any>({
    value: 'active',
    label: 'Active',
  });

  const debouncedSearch = useSearchDebounce(filters.search);

  // Restore chips from URL state on initial load
  useUpdateEffect(() => {
    if (!isRestoring && filters.status) {
      setChips([
        {
          label: 'Status',
          field: 'status',
          value: filters.status,
          displayValue: toTitleCase(filters.status),
        },
      ]);
      setStatusValue({
        value: filters.status,
        label: toTitleCase(filters.status),
      });
    }
  }, [isRestoring]);

  const sort = useMemo<UserMembershipSortInput[]>(() => {
    const field = SORT_KEY_MAP[filters.sortConfig.key] || 'CREATED_ON';
    const order = filters.sortConfig.direction === 'asc' ? 'ASC' : 'DESC';
    return [{ field, order } as UserMembershipSortInput];
  }, [filters.sortConfig]);

  const queryFilter = useMemo<UserMembershipQueryFilter>(() => {
    const base: UserMembershipQueryFilter = { membershipId, systemId };
    if (filters.status) {
      base[filters.status as StatusKey] = true;
    }
    return base;
  }, [membershipId, systemId, filters.status]);

  const {
    data: userMembershipsData,
    count,
    hasMore: hasNextPage,
    endCursor,
    isLoading,
    isRefetching,
  } = useUserMembershipsRelayQuery(queryFilter, {
    limit: filters.sizePerPage,
    after: afterCursor,
    sort,
    search: debouncedSearch || undefined,
    enabled: !!membershipId && isComplete,
  });

  // Map GraphQL data to legacy format and accumulate across pages
  useMemo(() => {
    if (!userMembershipsData?.length && !afterCursor) {
      setMembers([]);
      return;
    }
    if (!userMembershipsData?.length) return;
    const mapped = userMembershipsData.map(mapUserMembershipToLegacy);
    if (afterCursor) {
      setMembers((prev) => [...prev, ...mapped]);
    } else {
      setMembers(mapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMembershipsData]);

  const resetPagination = () => {
    setAfterCursor(null);
    setPageNumber(0);
  };

  const applyStatusFilter = useCallback(() => {
    if (filters.status !== statusValue.value) {
      const newStatus = statusValue.value || 'active';
      setMembers([]);
      resetPagination();
      setFilters({ ...filters, status: newStatus });
      setChips([
        {
          label: 'Status',
          field: 'status',
          value: newStatus,
          displayValue: toTitleCase(newStatus),
        },
      ]);
    }
  }, [filters, statusValue, setFilters]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      setChips((prev) => prev.filter((chip) => chip.label !== deletedGroup.label));

      if (deletedGroup.label === 'Status') {
        setStatusValue({});
        setMembers([]);
        resetPagination();
        setFilters({ ...filters, status: '' });
      }
    },
    [filters, setFilters]
  );

  const columns = useMemo(() => {
    return membershipUsersColumnsV2(authType);
  }, [authType]);

  return (
    <CardView
      content={
        <PaginationTable
          showFilter
          sortBy={filters.sortConfig.key}
          onSortChange={(sort) => {
            setMembers([]);
            resetPagination();
            setFilters({
              ...filters,
              sortConfig: {
                key: sort.key,
                direction: sort.direction as 'desc' | 'asc',
              },
            });
          }}
          chips={chips}
          handleChipDelete={handleChipDelete}
          onApplyFilter={applyStatusFilter}
          spaceFilters={
            <MovaticFieldGroupSingleSelectFilter
              label={'Status'}
              value={statusValue.value || ''}
              onChange={(newValue) => {
                const selectedOption = STATUS_OPTIONS.find(
                  (option) => option.value === newValue.target.value
                );
                setStatusValue({
                  value: selectedOption?.value,
                  label: selectedOption?.label,
                });
              }}
              options={STATUS_OPTIONS.map((option, index) => (
                <MenuItem key={index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            />
          }
          exportData={exportData}
          csvFileName={'User_memberships.csv'}
          onExport={() => setExportData(false)}
          items={members || []}
          noDataText={'There are no users on this membership at this time'}
          onRowCLick={(id: string) => history.push(`/memberships/user/${id}`)}
          columns={columns}
          totalItems={count}
          hasMore={hasNextPage}
          localSearch={false}
          isLoading={isLoading || isRefetching || !isComplete || isRestoring}
          onPageChange={(_page, newPage) => {
            const pageNumberAux = newPage + 1;
            if (members?.length && members.length / filters.sizePerPage < pageNumberAux) {
              setAfterCursor(endCursor);
            }
            setPageNumber(newPage);
          }}
          onRowsPerPageChange={(pageSizeString) => {
            const pageSize = Number(pageSizeString.target.value);
            setMembers([]);
            resetPagination();
            setFilters({
              ...filters,
              sizePerPage: pageSize,
            });
          }}
          page={pageNumber}
          rowsPerPage={filters.sizePerPage}
          searchText={filters.search}
          searchPlaceholder={'Search users on this membership'}
          handleSearch={(event) => {
            resetPagination();
            setFilters({
              ...filters,
              search: event.target.value,
            });
          }}
        />
      }
      headerActions={
        <IconButtonMenu
          buttons={[
            {
              label: 'Export',
              onClick: () => setExportData(true),
              startIcon: <FileDownloadIcon />,
            },
          ]}
        />
      }
    />
  );
};

export default ActiveMembershipList;
